import React, { useState } from 'react';
import { Text, Icon, Button } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { EnvironmentRuntime, ProjectEnvironment } from '../../services/projectEnvironment';
import { formatDate } from '../../utils/dateUtils';
import { isBusyStatus, isDeployed, isFailedStatus, runtimeAction, urlLabel } from './types';
import { EnvironmentBadge } from './StatusBadge';

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-bg-v2-md py-bg-v2-sm w-full">
    <Text variant="text-sm" color="secondary" className="w-[130px] shrink-0">
      {label}
    </Text>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

interface EnvironmentDetailsViewProps {
  environment: ProjectEnvironment;
  /** Live container state for this environment, when known. */
  runtime?: EnvironmentRuntime;
  /** True while this environment's start/stop request is in flight. */
  runtimeActionPending: boolean;
  checkingDeploy: boolean;
  deleting: boolean;
  isBuilding: boolean;
  /** True while any environment of this project is building — only one deploy runs at a time. */
  /** Another environment of this project is mid-deploy. Only a deploy blocks a
   *  deploy — a `creating` sibling does no build and holds nothing up. */
  deployLocked: boolean;
  onStartStop: (action: 'start' | 'stop') => void;
  onDeploy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  project: DevProject | null;
}

/** Read-only detail screen for one environment + its Deploy/Edit/Delete actions. */
export const EnvironmentDetailsView: React.FC<EnvironmentDetailsViewProps> = ({
  environment,
  runtime,
  runtimeActionPending,
  checkingDeploy,
  deleting,
  isBuilding,
  deployLocked,
  onStartStop,
  onDeploy,
  onEdit,
  onDelete,
  project,
}) => {
  const [copiedError, setCopiedError] = useState(false);
  const handleCopyErrorDetail = async () => {
    if (!environment.error_detail) return;
    try {
      await navigator.clipboard.writeText(environment.error_detail);
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  const busy = isBusyStatus(environment.status);
  const deployed = isDeployed(environment.status);
  const mutationsLocked = busy || deleting;
  const action = deployed && !busy ? runtimeAction(runtime, environment.status) : null;
  const actionPending = runtimeActionPending || runtime?.in_progress === true;
  const deployDisabled =
    mutationsLocked || checkingDeploy || isBuilding || deployLocked || actionPending;
  const stopped = deployed && runtime?.status === 'stopped';

  const deployTitle = isBuilding
    ? 'Unavailable while a build is running'
    : deployLocked
    ? 'Another deploy is already running for this project'
    : actionPending
    ? 'Unavailable while the environment is starting or stopping'
    : 'Deploy current changes';

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full">
      {/* Status + tiny actions */}
      <div className="flex items-center gap-bg-v2-md">
        <div className="flex items-center gap-bg-v2-md flex-wrap flex-1 min-w-0">
          <EnvironmentBadge status={environment.status} runtime={runtime} />
        </div>

        <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
          <div className="flex items-center gap-bg-v2-xs shrink-0">
            <button
              onClick={onEdit}
              disabled={mutationsLocked}
              title={busy ? 'Unavailable while a build is running' : 'Edit environment'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
            >
              <Icon name={IconName.Edit} size="xs" className="text-text-tertiary" inheritColor />
            </button>
            <button
              onClick={onDelete}
              disabled={mutationsLocked}
              title={busy ? 'Unavailable while a build is running' : 'Delete environment'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-error-primary transition-colors disabled:opacity-50"
            >
              <Icon name={deleting ? IconName.Loading : IconName.Delete} size="xs" spin={deleting} className="text-text-tertiary hover:text-text-error-primary transition-colors" inheritColor />
            </button>
          </div>
        </IfHasPermission>
      </div>

      {/* URL — hidden entirely when the environment failed */}
      {deployed && environment.url && (
        <a
          href={environment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-bg-v2-xs text-border-brand hover:underline min-w-0 max-w-full"
        >
          <Text variant="text-sm" className="!text-border-brand truncate">
            {urlLabel(environment.url)}
          </Text>
          <Icon name={IconName.ExternalLink} size="xs" inheritColor className="shrink-0" />
        </a>
      )}
      {busy && (
        <Text variant="text-sm" color="tertiary" className="truncate max-w-full">
          {environment.url ? `${urlLabel(environment.url)} · available once deployed` : 'URL available once deployed'}
        </Text>
      )}

      {busy && (
        <div className="flex items-start gap-bg-v2-xs p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-md">
          <Icon name={IconName.Loading} size="sm" spin className="text-text-brand-primary shrink-0 mt-bg-v2-xxs" inheritColor />
          <Text variant="text-xs" color="secondary" className="break-words">
            A deploy is running. This involves a Docker build and can take several minutes.
          </Text>
        </div>
      )}

      {stopped && (
        <div className="flex items-start gap-bg-v2-xs p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-md">
          <Icon name={IconName.InfoCircle} size="sm" className="text-text-tertiary shrink-0 mt-bg-v2-xxs" inheritColor />
          <Text variant="text-xs" color="secondary" className="break-words">
            This environment is stopped, so its URL won't respond. Starting it brings the app
            back up in seconds — its data is kept.
          </Text>
        </div>
      )}

      {isFailedStatus(environment.status) && (
        <div className="flex flex-col gap-bg-v2-sm p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <div className="flex items-start gap-bg-v2-xs">
            <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0 mt-bg-v2-xxs" inheritColor />
            <Text variant="text-xs" color="error" className="break-words">
              {environment.error ||
                'The last deploy failed. Fix the project code and deploy again.'}
            </Text>
          </div>

          {/* The raw build output is a wall of compiler noise — too wide for a
              480px panel to render usefully. Copying it out lets the user paste
              it somewhere that can actually display it, or hand it to support. */}
          {environment.error_detail && (
            <div className="flex items-start">
              <Button
                variant="link"
                size="small"
                onClick={handleCopyErrorDetail}
                title="Copy the full build output to the clipboard"
                label={
                  <div className="flex items-center gap-bg-v2-xs">
                    <Icon
                      name={copiedError ? IconName.Check : IconName.Copy}
                      size="sm"
                      inheritColor
                    />
                    <span>{copiedError ? 'Copied' : 'Copy error details'}</span>
                  </div>
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Details */}
      <div className="flex flex-col w-full">
        <Text variant="text-sm" weight="medium" color="primary" className="mb-bg-v2-xs">
          Details
        </Text>
        <DetailRow label="Deployed at">
          <Text variant="text-sm" color="primary">
            {environment.deployed_at ? formatDate(environment.deployed_at) : 'Not deployed yet'}
          </Text>
        </DetailRow>
        {environment.deployed_commit_hash && (
          <DetailRow label="Version">
            <Text variant="text-sm" color="primary" className="font-mono">
              {environment.deployed_commit_hash.slice(0, 7)}
            </Text>
          </DetailRow>
        )}
        {environment.updated_by && (
          <DetailRow label="Last deployed by">
            <Text variant="text-sm" color="primary" className="break-words">
              {environment.updated_by}
            </Text>
          </DetailRow>
        )}
        {environment.created_by && (
          <DetailRow label="Created by">
            <Text variant="text-sm" color="primary" className="break-words">
              {environment.created_by}
            </Text>
          </DetailRow>
        )}
        <DetailRow label="Created">
          <Text variant="text-sm" color="primary">
            {environment.created_at ? formatDate(environment.created_at) : '—'}
          </Text>
        </DetailRow>
      </div>

      {/* Start/Stop + Deploy — right aligned, after the details */}
      <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
        <div className="flex justify-end gap-bg-v2-md pt-bg-v2-md border-t border-border-secondary">
          {deployed && (action || actionPending) && (
            <Button
              variant={action === 'stop' || runtime?.status === 'stopping' ? 'error' : 'success'}
              size="small"
              onClick={() => action && onStartStop(action)}
              disabled={!action || actionPending}
              loading={actionPending}
              title={
                actionPending || !action
                  ? 'Start or stop in progress'
                  : action === 'start'
                  ? 'Start this environment'
                  : 'Stop this environment — its data is kept'
              }
              label={
                <div className="flex items-center gap-bg-v2-xs">
                  {!actionPending && (
                    <Icon
                      name={action === 'start' ? IconName.Run : IconName.Stop}
                      size="sm"
                      inheritColor
                    />
                  )}
                  <span>
                    {actionPending
                      ? runtime?.status === 'stopping'
                        ? 'Stopping…'
                        : 'Starting…'
                      : action === 'start'
                      ? 'Start'
                      : 'Stop'}
                  </span>
                </div>
              }
            />
          )}
          <Button
            variant="primary"
            size="small"
            onClick={onDeploy}
            disabled={deployDisabled}
            loading={checkingDeploy}
            title={deployTitle}
            label={
              <div className="flex items-center gap-bg-v2-xs">
                <Icon name={IconName.Push} size="sm" inheritColor />
                <span>{checkingDeploy ? 'Checking…' : 'Deploy current changes'}</span>
              </div>
            }
          />
        </div>
      </IfHasPermission>
    </div>
  );
};

export default EnvironmentDetailsView;
